import React, { useState } from "react";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, pointerWithin,
} from "@dnd-kit/core";
import { useDayRender } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarClock, Pencil, Eye, GripVertical } from "lucide-react";
import { toast } from "sonner";

const startOfDayLocal = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const sameDay = (a, b) => !!a && !!b && startOfDayLocal(a).getTime() === startOfDayLocal(b).getTime();
const dayKey = (d) => startOfDayLocal(d).toISOString().slice(0, 10);
const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

// A calendar day cell that is also a drop target for scheduled-article cards.
function DroppableDay(props) {
  const buttonRef = React.useRef(null);
  const render = useDayRender(props.date, props.displayMonth, buttonRef);
  const { setNodeRef, isOver } = useDroppable({ id: `day-${dayKey(props.date)}`, data: { date: props.date } });

  if (render.isHidden) return <div role="gridcell" />;
  if (!render.isButton) return <div {...render.divProps} />;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-md transition-shadow ${isOver ? "ring-2 ring-[#c9a86a] ring-offset-1 ring-offset-[var(--panel)] bg-[#c9a86a]/10" : ""}`}
      data-testid={`cal-day-${dayKey(props.date)}`}
    >
      <button name="day" ref={buttonRef} {...render.buttonProps} />
    </div>
  );
}

// A draggable scheduled-article card (drag by the grip handle).
function ScheduleCard({ a, editable, onEdit, onPublishNow, selected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: a.id });
  return (
    <li
      ref={setNodeRef}
      className={`flex items-center justify-between gap-3 rounded-md border bg-[var(--panel)] p-3 ${selected ? "border-[#c9a86a]" : "border-[var(--border-lch)]"} ${isDragging ? "opacity-40" : ""}`}
      data-testid={`schedule-item-${a.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {editable && (
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect(a.id)}
            aria-label="Select article"
            data-testid={`schedule-select-${a.id}`}
          />
        )}
        {editable && (
          <button
            className="cursor-grab active:cursor-grabbing touch-none text-[var(--muted)] hover:text-[var(--text)] shrink-0"
            {...listeners}
            {...attributes}
            aria-label="Drag to reschedule"
            data-testid={`schedule-drag-${a.id}`}
          >
            <GripVertical size={16} />
          </button>
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">{a.title}</div>
          <div className="text-xs text-[#9a7b3f] flex items-center gap-1 mt-0.5"><CalendarClock size={12} /> {fmtDateTime(a.publish_at)}</div>
          <div className="text-xs text-[var(--muted)]">{a.category || "\u2014"}{" \u00B7 "}{a.reading_time || 1} min</div>
        </div>
      </div>
      {editable && (
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onEdit(a)} data-testid={`schedule-edit-${a.id}`}><Pencil size={14} className="mr-1" />Edit</Button>
          <Button variant="outline" size="sm" onClick={() => onPublishNow(a)} data-testid={`schedule-publish-now-${a.id}`} className="bg-[var(--panel-2)]"><Eye size={14} className="mr-1" />Publish now</Button>
        </div>
      )}
    </li>
  );
}

export default function ScheduleCalendar({ scheduledItems, selectedDay, onSelectDay, editable, onReschedule, onEdit, onPublishNow, onBulkReschedule }) {
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const scheduledDays = scheduledItems.map((a) => startOfDayLocal(new Date(a.publish_at)));
  const list = selectedDay ? scheduledItems.filter((a) => sameDay(new Date(a.publish_at), selectedDay)) : scheduledItems;
  const activeItem = scheduledItems.find((a) => a.id === activeId) || null;

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const clearSelection = () => setSelected([]);
  const selectedItems = scheduledItems.filter((a) => selected.includes(a.id));

  const applyBulk = (transform) => {
    const now = Date.now();
    const items = [];
    let pastCount = 0;
    for (const a of selectedItems) {
      const nd = transform(new Date(a.publish_at));
      if (!nd || isNaN(nd.getTime())) continue;
      if (nd.getTime() <= now) { pastCount += 1; continue; }
      items.push({ id: a.id, publish_at: nd.toISOString() });
    }
    if (!items.length) {
      toast.error(pastCount ? "That would put the selected article(s) in the past" : "Nothing to reschedule");
      return;
    }
    onBulkReschedule(items);
    clearSelection();
  };

  const shiftDays = (days) => applyBulk((d) => { const n = new Date(d); n.setDate(n.getDate() + days); return n; });
  const moveToSelectedDay = () => {
    if (!selectedDay) return;
    applyBulk((d) => { const n = new Date(d); n.setFullYear(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate()); return n; });
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !active) return;
    const date = over.data?.current?.date;
    const a = scheduledItems.find((x) => x.id === active.id);
    if (!date || !a) return;
    const orig = new Date(a.publish_at);
    const nd = new Date(date);
    nd.setHours(orig.getHours(), orig.getMinutes(), 0, 0); // keep the original time-of-day
    if (sameDay(nd, orig)) return; // dropped on the same day
    if (nd.getTime() <= Date.now()) { toast.error("Pick a future day to reschedule"); return; }
    onReschedule(a.id, nd.toISOString());
  };

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={(e) => setActiveId(e.active.id)} onDragCancel={() => setActiveId(null)} onDragEnd={handleDragEnd}>
      <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start" data-testid="journal-calendar-view">
        <Card className="border-[var(--border-lch)] p-3 w-fit" data-testid="journal-calendar">
          <Calendar
            mode="single"
            selected={selectedDay || undefined}
            onSelect={onSelectDay}
            defaultMonth={scheduledDays[0] || new Date()}
            modifiers={{ scheduled: scheduledDays }}
            modifiersStyles={{ scheduled: { fontWeight: 700, color: "#9a7b3f", boxShadow: "inset 0 -2px 0 0 #c9a86a", borderRadius: "6px" } }}
            components={{
              IconLeft: (p) => <ChevronLeft className="h-4 w-4" {...p} />,
              IconRight: (p) => <ChevronRight className="h-4 w-4" {...p} />,
              Day: DroppableDay,
            }}
          />
          <div className="px-2 pb-1 pt-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-[var(--text-2)]">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#c9a86a]" /> Scheduled day
            </span>
            {selectedDay && (
              <button onClick={() => onSelectDay(null)} className="text-xs text-[#9a7b3f] hover:underline" data-testid="journal-calendar-clear">Show all</button>
            )}
          </div>
          {editable && (
            <p className="px-2 pt-1 text-[11px] text-[var(--muted)]">Tip: drag an article onto a day to reschedule it.</p>
          )}
        </Card>

        <Card className="border-[var(--border-lch)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <CalendarClock size={16} className="text-[#c9a86a]" />
              {selectedDay ? `Scheduled on ${selectedDay.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : "Upcoming scheduled"}
            </h2>
            <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">{list.length}</Badge>
          </div>

          {editable && selected.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[#c9a86a]/40 bg-[var(--panel-2)] p-2.5" data-testid="bulk-reschedule-bar">
              <span className="text-xs font-medium text-[var(--text)]" data-testid="bulk-selected-count">{selected.length} selected</span>
              <span className="text-[11px] text-[var(--muted)]">Reschedule:</span>
              <Button size="sm" variant="outline" className="h-7 bg-[var(--panel)]" onClick={() => shiftDays(-1)} data-testid="bulk-shift-minus1">{"\u2212"}1 day</Button>
              <Button size="sm" variant="outline" className="h-7 bg-[var(--panel)]" onClick={() => shiftDays(1)} data-testid="bulk-shift-plus1">+1 day</Button>
              <Button size="sm" variant="outline" className="h-7 bg-[var(--panel)]" onClick={() => shiftDays(7)} data-testid="bulk-shift-plus7">+7 days</Button>
              {selectedDay && (
                <Button size="sm" className="h-7 bg-[#c9a86a] text-[var(--on-gold)] hover:bg-[#b89455]" onClick={moveToSelectedDay} data-testid="bulk-move-to-day">
                  Move to {selectedDay.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </Button>
              )}
              <button onClick={clearSelection} className="ml-auto text-xs text-[#9a7b3f] hover:underline" data-testid="bulk-clear">Clear</button>
            </div>
          )}

          {list.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--muted)]" data-testid="journal-calendar-empty">
              {selectedDay ? "Nothing scheduled for this day." : "No upcoming scheduled articles. Set an article's status to \u201CScheduled\u201D to plan ahead."}
            </div>
          ) : (
            <ul className="space-y-2">
              {list.map((a) => (
                <ScheduleCard
                  key={a.id}
                  a={a}
                  editable={editable}
                  onEdit={onEdit}
                  onPublishNow={onPublishNow}
                  selected={selected.includes(a.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </ul>
          )}
        </Card>
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="rounded-md border border-[#c9a86a] bg-[var(--panel-2)] px-3 py-2 text-sm font-medium text-[var(--text)] shadow-xl flex items-center gap-2">
            <GripVertical size={14} className="text-[#c9a86a]" /> {activeItem.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
